import { TROPHY_TYPE, TrophyType } from "./trophyType.const";

export type SimpleTrophyRequirement = number[];

const TEST_REQUIREMENTS: SimpleTrophyRequirement = [1, 2, 3, 4];

const PRODUCTION_TROPHY_REQUIREMENTS: Record<TrophyType, SimpleTrophyRequirement> = {
  // POWER_SIGN
  [TROPHY_TYPE.STARTER]: [1],
  [TROPHY_TYPE.SOCIAL_MOTIVATOR]: [2, 4, 6, 8],
  [TROPHY_TYPE.MOTIVATION_AMBASSADOR]: [2, 4, 6, 8],
  [TROPHY_TYPE.LESSON_SPRINTER]: [2, 4, 6, 8],
  [TROPHY_TYPE.DAILY_RECORD_HOLDER]: [2, 3, 5, 7],
  [TROPHY_TYPE.VIRTUAL_EXPLORER]: [1, 2, 3, 4],
  [TROPHY_TYPE.PERSISTENT_WANDERER]: [3, 6, 12, 24],
  [TROPHY_TYPE.CONSTRUCTIVE_REVIEWER]: [1, 2, 3, 4],
  // LEARNING_TROPHY
  [TROPHY_TYPE.MATH_MASTER]: [1, 3, 4, 5],
  [TROPHY_TYPE.POLISH_MASTER]: [1, 2, 3, 4],
  [TROPHY_TYPE.ENGLISH_MASTER]: [1, 3, 4, 6],
  [TROPHY_TYPE.MATH_QUIZ_MARATHONER]: [10, 20, 25, 30],
  [TROPHY_TYPE.POLISH_QUIZ_MARATHONER]: [10, 20, 25, 30],
  [TROPHY_TYPE.ENGLISH_QUIZ_MARATHONER]: [10, 20, 25, 30],
  [TROPHY_TYPE.MATH_PRECISE_MIND]: [10, 20, 25, 30],
  [TROPHY_TYPE.POLISH_PRECISE_MIND]: [10, 20, 25, 30],
  [TROPHY_TYPE.ENGLISH_PRECISE_MIND]: [10, 20, 25, 30],
  [TROPHY_TYPE.MATH_FLAWLESS_MIND]: [5, 10, 20, 30],
  [TROPHY_TYPE.POLISH_FLAWLESS_MIND]: [5, 10, 20, 30],
  [TROPHY_TYPE.ENGLISH_FLAWLESS_MIND]: [5, 10, 20, 30],
  [TROPHY_TYPE.MATH_VIDEO_ENTHUSIAST]: [5, 10, 20, 30],
  [TROPHY_TYPE.POLISH_VIDEO_ENTHUSIAST]: [5, 10, 20, 30],
  [TROPHY_TYPE.ENGLISH_VIDEO_ENTHUSIAST]: [5, 10, 20, 30],
  [TROPHY_TYPE.MATH_DIDACTIC_COLLECTOR]: [10, 20, 25, 30],
  [TROPHY_TYPE.POLISH_DIDACTIC_COLLECTOR]: [10, 20, 25, 30],
  [TROPHY_TYPE.ENGLISH_DIDACTIC_COLLECTOR]: [10, 20, 25, 30],
  // BIDIRECTIONAL_TROPHY
  [TROPHY_TYPE.TWO_SUBJECT_MASTER_MAT_ENG]: [1, 2, 4, 6],
  [TROPHY_TYPE.TWO_SUBJECT_MASTER_MAT_POL]: [1, 2, 4, 6],
  [TROPHY_TYPE.TWO_SUBJECT_MASTER_ENG_POL]: [1, 2, 4, 6],
  [TROPHY_TYPE.TWO_SECTION_EXPERT_MAT_ENG]: [1, 2, 3],
  [TROPHY_TYPE.TWO_SECTION_EXPERT_MAT_POL]: [1, 2, 3],
  [TROPHY_TYPE.TWO_SECTION_EXPERT_ENG_POL]: [1, 2, 3],
  [TROPHY_TYPE.QUIZ_DUO_MAT_ENG]: [5, 10, 15, 20],
  [TROPHY_TYPE.QUIZ_DUO_MAT_POL]: [5, 10, 15, 20],
  [TROPHY_TYPE.QUIZ_DUO_ENG_POL]: [5, 10, 15, 20],
  [TROPHY_TYPE.FLAWLESS_DUO_MAT_ENG]: [2, 5, 10, 20],
  [TROPHY_TYPE.FLAWLESS_DUO_MAT_POL]: [2, 5, 10, 20],
  [TROPHY_TYPE.FLAWLESS_DUO_ENG_POL]: [2, 5, 10, 20],
  [TROPHY_TYPE.VIDEO_DUO_MAT_ENG]: [2, 5, 10, 20],
  [TROPHY_TYPE.VIDEO_DUO_MAT_POL]: [2, 5, 10, 20],
  [TROPHY_TYPE.VIDEO_DUO_ENG_POL]: [2, 5, 10, 20],
  [TROPHY_TYPE.DIDACTIC_DUO_MAT_ENG]: [5, 10, 20, 25],
  [TROPHY_TYPE.DIDACTIC_DUO_MAT_POL]: [5, 10, 20, 25],
  [TROPHY_TYPE.DIDACTIC_DUO_ENG_POL]: [5, 10, 20, 25],
  // INTERDISCIPLINARY_TROPHY
  [TROPHY_TYPE.THREE_SUBJECT_MASTER]: [1, 2, 4, 6],
  [TROPHY_TYPE.THREE_SECTION_EXPERT]: [1, 2, 3],
  [TROPHY_TYPE.QUIZ_TRIATHLON]: [5, 10, 15, 20],
  [TROPHY_TYPE.FLAWLESS_TRIO]: [1, 2, 4, 6],
  [TROPHY_TYPE.VIDEO_TRIAD]: [1, 2, 4, 6],
  [TROPHY_TYPE.DIDACTIC_TRIAD]: [1, 2, 3, 4],
};

// Create test requirements object with simplified values
const createTestRequirements = (): Record<TrophyType, SimpleTrophyRequirement> => {
  const testRequirements = {} as Record<TrophyType, SimpleTrophyRequirement>;

  for (const [trophyType, originalRequirements] of Object.entries(PRODUCTION_TROPHY_REQUIREMENTS)) {
    if (originalRequirements.length === 1) {
      testRequirements[trophyType as TrophyType] = [1];
    } else if (originalRequirements.length === 3) {
      testRequirements[trophyType as TrophyType] = [1, 2, 3];
    } else {
      testRequirements[trophyType as TrophyType] = TEST_REQUIREMENTS;
    }
  }

  return testRequirements;
};

export const TROPHY_REQUIREMENTS: Record<TrophyType, SimpleTrophyRequirement> =
  PRODUCTION_TROPHY_REQUIREMENTS;

const TEST_TROPHY_REQUIREMENTS: Record<TrophyType, SimpleTrophyRequirement> =
  createTestRequirements();

export const getTrophyRequirements = (
  isTest: boolean = false,
): Record<TrophyType, SimpleTrophyRequirement> => {
  return isTest ? TEST_TROPHY_REQUIREMENTS : PRODUCTION_TROPHY_REQUIREMENTS;
};

export const TROPHY_REQUIREMENTS_PERCENTAGE_REQUIRED: Record<
  | typeof TROPHY_TYPE.MATH_PRECISE_MIND
  | typeof TROPHY_TYPE.POLISH_PRECISE_MIND
  | typeof TROPHY_TYPE.ENGLISH_PRECISE_MIND,
  SimpleTrophyRequirement
> = {
  [TROPHY_TYPE.MATH_PRECISE_MIND]: [80, 85, 90, 95],
  [TROPHY_TYPE.POLISH_PRECISE_MIND]: [80, 85, 90, 95],
  [TROPHY_TYPE.ENGLISH_PRECISE_MIND]: [80, 85, 90, 95],
} as const;
